/**
 * The Microsoft 365 service-health board.
 *
 * Every number here comes from Microsoft Graph's service-announcement API, read
 * by the NOVA backend with an app-only token and served from a server-side
 * cache — the browser never talks to Graph and never holds a Graph credential.
 *
 * The board has two honest states and no third: either it is showing what
 * Microsoft reported, or it is explaining why it cannot. It never fills the gap
 * with placeholder services or sample incidents.
 *
 * There is a third thing it can be, orthogonal to those two: **partial**. Graph
 * can answer, and part of that answer can still be unreadable — a Message
 * Center post whose body came back in an unexpected shape, an entry that would
 * not parse. `status_reason` stays `"ok"`, so the board is showing what
 * Microsoft reported *minus whatever was dropped*, and every count on it is a
 * lower bound. `errors[]` is how the backend says so, and `M365ErrorsAlert` is
 * how the board repeats it — above the figures, never collapsed, and naming the
 * figures it makes a floor. Partial composes with both honest states rather
 * than replacing either: an unconfigured board that also hit a read problem
 * shows the setup panel with the errors nested inside it, so there is one
 * explanation and not two competing ones.
 *
 * Only `/m365/service-health` is read here, and that is deliberate rather than
 * incidental: every panel below — including the Message Center feed and the
 * time-to-restore table — is projected from that single payload, so no panel can
 * disagree with another about what Microsoft said, and adding one costs no extra
 * Graph call. `/m365/incidents` and `/m365/messages` re-serve slices of the same
 * cached snapshot for other consumers (the employee-facing maintenance feed) and
 * have none in this feature. When either gets its own reader, that reader
 * renders its own `M365ErrorsAlert`.
 */

import { Alert, Freshness, PageHeader, Skeleton } from "@norican/react";

import { NAV_SECTIONS } from "@/components/nav-sections";
import { useM365ServiceHealth } from "@/lib/api";

import { affectedFigures, M365ErrorsAlert } from "./errors-alert";
import { IncidentTable } from "./incident-table";
import { MessagesPanel } from "./messages-panel";
import { MttrPanel } from "./mttr-panel";
import { M365SetupPanel } from "./not-configured";
import { ResolvedPanel } from "./resolved-panel";
import { ServiceTiles } from "./service-tiles";
import { SummaryStrip } from "./summary-strip";

const SECTION = NAV_SECTIONS.find((section) => section.label === "M365");

export function M365Page() {
  const { data, isPending, error } = useM365ServiceHealth();

  const live = data?.status_reason === "ok";
  // The backend refreshes on its own TTL; flag the data as stale once it is
  // meaningfully older than one refresh window rather than the instant it ages.
  const staleAfterMs = data ? data.cache_ttl_sec * 1000 * 2 : undefined;

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Microsoft 365"
        subtitle={
          SECTION?.description ??
          "Tenant service health, read from Microsoft Graph by the NOVA backend."
        }
        actions={
          data ? (
            <Freshness
              label={data.last_updated_at ? "Graph read" : "Never read"}
              since={data.last_updated_at ?? undefined}
              staleAfter={data.last_updated_at ? staleAfterMs : undefined}
              tone={data.last_updated_at ? undefined : "neutral"}
            />
          ) : undefined
        }
      />

      {error && (
        <Alert tone="danger" title="Couldn't load the M365 board">
          {error instanceof Error
            ? error.message
            : "The NOVA API did not respond. This is a NOVA problem, not a Microsoft one."}
        </Alert>
      )}

      {isPending && !data ? (
        <div className="flex flex-col gap-4" aria-busy="true">
          <Skeleton height="6rem" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} height="7rem" />
            ))}
          </div>
          <Skeleton height="18rem" />
        </div>
      ) : (
        data && (
          <>
            {!live && <M365SetupPanel health={data} />}
            {live && (
              <>
                {/* Above the strip, so the caveat is read before the numbers it
                    qualifies — not after them, and not behind a click. */}
                <M365ErrorsAlert errors={data.errors} affects={affectedFigures(data)} />
                <SummaryStrip
                  summary={data.summary}
                  resolvedWindowDays={data.resolved_window_days}
                />
                <ServiceTiles services={data.services} />
                <IncidentTable incidents={data.active_incidents} />
                <ResolvedPanel
                  incidents={data.resolved_recently}
                  windowDays={data.resolved_window_days}
                />
                {/* Looks backwards where everything above it looks at now — so
                    it sits after the current-state panels, not among them. */}
                <MttrPanel mttr={data.mttr} />
                {/* Owns all three Message Center states, including the
                    missing-permission one this board used to render inline. */}
                <MessagesPanel
                  messages={data.messages}
                  available={data.messages_available}
                  missingScopes={data.missing_scopes}
                  hasReadErrors={data.errors.length > 0}
                />
              </>
            )}
          </>
        )
      )}
    </main>
  );
}
